"use client";

import React, { useRef, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";

// local component imports
import CustomImage from "../../../../../../components/atoms/customImage";
import { MuteIcon, UnmuteIcon, ShareIcon, PlayIcon, PauseIcon } from "./Icons";

/**
 * DesktopFullScreen renders a fullscreen modal with a video player and optional tagged product sidebar.
 * @param {object} props - Component props
 * @param {Array} props.videos - Array of video objects to display
 * @param {number} props.initialIndex - Index of the video to show first
 * @param {Function} props.onClose - Callback to close the modal
 * @returns {React.ReactElement} Portal-rendered fullscreen modal
 */
const DesktopFullScreen = ({ videos = [], initialIndex = 0, onClose, videoProducts = [] }) => {
  const videoRef = useRef(null);
  const hideTimeout = useRef();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [showPlayPause, setShowPlayPause] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const video = videos[currentIndex] || videos[0];
  const products = videoProducts?.[currentIndex] ?? [];

  /**
   * Triggers the close animation then calls onClose.
   */
  const onCloseModal = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => onClose(), 250);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Auto-play on mount
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  }, []);

  // Close on Escape key
  useEffect(() => {
    /**
     * Handles keydown events, closing the modal on Escape.
     * @param {KeyboardEvent} e - The keyboard event
     */
    const onKeyDown = (e) => {
      if (e.key === "Escape") onCloseModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCloseModal]);

  // Reset video playback when index changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  }, [currentIndex]);

  /**
   * Toggles video play/pause and briefly shows the play/pause indicator.
   */
  const onTogglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
    setShowPlayPause(true);
    clearTimeout(hideTimeout.current);
    hideTimeout.current = setTimeout(() => setShowPlayPause(false), 800);
  };

  /**
   * Toggles mute state on the video element.
   */
  const onToggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted((prev) => !prev);
    }
  }, [isMuted]);

  /**
   * Shares the current page URL via the Web Share API.
   */
  const onShare = () => {
    navigator.share?.({ url: window.location.href });
  };

  /**
   * Navigates to the footer CTA URL.
   */
  const onFooterCtaClick = () => {
    if (video?.footer?.linkUrl) {
      window.open(video.footer.linkUrl, "_blank", "noopener,noreferrer");
    }
  };

  /**
   * Closes the modal when clicking the backdrop (not the content).
   * @param {MouseEvent} e - The click event
   */
  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) onCloseModal();
  };

  /**
   * Navigates to the previous video.
   */
  const onPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setIsPlaying(true);
    }
  };

  /**
   * Navigates to the next video.
   */
  const onNext = () => {
    if (currentIndex < videos.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setIsPlaying(true);
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-[#202020]/50 backdrop-blur-md ${
        isClosing ? "animate-backdropOut" : "animate-backdropIn"
      }`}
      onClick={onBackdropClick}
    >
      <button
        type="button"
        onClick={onCloseModal}
        className="border-black/20 absolute right-6 top-6 z-[10000] flex h-[44px] w-[44px] items-center justify-center rounded-full border bg-[#000000] transition-all hover:scale-110"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="black">
          <path
            d="M18 6L6 18M6 6l12 12"
            stroke="white"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {currentIndex > 0 ? (
        <button
          type="button"
          onClick={onPrevious}
          className="hover:bg-black/80 mr-[15px] flex h-[48px] w-[48px] items-center justify-center rounded-full bg-[#202020] backdrop-blur-sm transition-all"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M15 18l-6-6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      ) : (
        <button
          type="button"
          onClick={onPrevious}
          className="mr-[15px] flex h-[48px] w-[48px] items-center justify-center rounded-full bg-transparent transition-all"
        >
          <></>
        </button>
      )}

      <div
        className={`flex items-center ${products?.length > 0 ? "bg-[#ffffff]" : "bg-transparent"} gap-6 rounded-[20px] px-[28px] py-[28px] ${isClosing ? "animate-modalOut" : "animate-modalIn"}`}
      >
        <div className="flex flex-col items-center">
          <div
            className="bg-black ring-white/10 relative overflow-hidden rounded-3xl shadow-2xl ring-1"
            style={{ height: "85vh", width: "calc(85vh * 9 / 16)", maxWidth: "410px" }}
          >
            <video
              ref={videoRef}
              src={video.videoUrl}
              poster={video.thumbnail}
              className="absolute inset-0 h-full w-full object-cover"
              muted={isMuted}
              loop={true}
              playsInline={true}
              onClick={onTogglePlay}
            />

            {showPlayPause && (
              <div className="bg-black/40 pointer-events-none absolute left-1/2 top-1/2 flex h-[72px] w-[72px] -translate-x-1/2 -translate-y-1/2 animate-fadeOut items-center justify-center rounded-full">
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
              </div>
            )}

            <div className="absolute left-3 right-3 top-3 flex items-center justify-end">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onToggleMute}
                  className="bg-white/10 flex h-[36px] w-[36px] items-center justify-center rounded-full"
                >
                  {isMuted ? <MuteIcon /> : <UnmuteIcon />}
                </button>
                <button
                  type="button"
                  onClick={onShare}
                  className="bg-white/10 flex h-[36px] w-[36px] items-center justify-center rounded-full"
                >
                  <ShareIcon />
                </button>
              </div>
            </div>

            {!isPlaying && (
              <button
                type="button"
                onClick={onTogglePlay}
                className="absolute left-1/2 top-1/2 flex h-[48px] w-[48px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[rgba(15,23,43,0.24)]"
              >
                <PlayIcon />
              </button>
            )}
          </div>

          {video?.showCta && video?.footer && (
            <button
              type="button"
              onClick={onFooterCtaClick}
              className="mt-[16px] flex w-[calc(100%-0px)] items-center justify-center gap-[5px] rounded-xl px-4 py-[12px]"
              style={{
                backgroundColor: video.footer.backgroundColor || "#ffffff",
                color: video.footer.textColor || "#1A1A1A",
                borderRadius: video.footer.borderRadius ?? 8,
                border: `${video.footer.borderThickness ?? 0.5}px solid ${video.footer.borderColor || "#1A1A1A"}`,
              }}
            >
              <span className="text-sm font-semibold">{video.footer.text}</span>
              {video.footer.showArrow && <span style={{ marginLeft: "3px", fontSize: "1.125rem" }}>&#8250;</span>}
            </button>
          )}
        </div>

        {products?.length > 0 && (
          <div
            className="bg-white flex w-[322px] flex-col gap-[15px] overflow-y-auto rounded-2xl bg-[#ffffff] pl-[15px]"
            style={{ height: "85vh", maxHeight: "85vh" }}
          >
            {products.map((product, index) => (
              <div
                key={product.productId || index}
                className="bg-white flex cursor-pointer items-center gap-[8px] rounded-xl border border-[#EEE] p-[10px] transition-all hover:scale-[1.02]"
                onClick={() => product.productUrl && window.open(product.productUrl, "_blank", "noopener,noreferrer")}
              >
                <div className="relative h-[80px] w-[80px] shrink-0 overflow-hidden rounded-lg">
                  <CustomImage
                    src={product.productImage?.path || product.productImage}
                    alt={product.productName || "Product"}
                    fill={true}
                    className="object-cover"
                    sizes="80px"
                  />
                </div>
                <div className=" min-w-0 flex-1 self-start">
                  <p className="mb-[5px] line-clamp-2 overflow-hidden text-ellipsis font-['Inter'] text-[14px] font-normal leading-[18px] tracking-[0.1px] text-[#191A0B]">
                    {product.productName || "Product"}
                  </p>
                  {product.price?.listPrice && (
                    <p className="font-['Inter'] text-[16px] font-semibold leading-[24px] text-[#191A0B]">
                      ₹{Math.floor(product.price.listPrice)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {currentIndex < videos.length - 1 && (
        <button
          type="button"
          onClick={onNext}
          className="hover:bg-black/80 ml-[15px] flex h-[48px] w-[48px] items-center justify-center rounded-full bg-[#202020] backdrop-blur-sm transition-all"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </div>,
    document.body,
  );
};

DesktopFullScreen.propTypes = {
  videos: PropTypes.arrayOf(PropTypes.object),
  initialIndex: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  videoProducts: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)),
};

export default DesktopFullScreen;
